import { GridColDef, GridRenderCellParams } from "esseal-data-table";;
import { Button, Checkbox } from "@mui/material";
import { User, EmploymentStatusEnum, PermissionActionEnum, PermissionEntityEnum } from "@/graphql/generated/graphql";
import EssealPhoneInput from "@/components/EssealPhoneInput";
import { ClearOutlined, RestoreOutlined } from "@mui/icons-material";
import PermissionContext from "@/Contexts/Permissions";
import { useContext } from "react";
import RequestPermissionTooltip from "@/components/RequestPermissionTooltip";

const usersColumns = (
  _canAddTags: boolean,
  handleOnClickRemoveResource: (resource: User) => void,
  handleOnUpdateEmployee: (updatedRow: User, originalRow: User) => void,
  currentUserId?: string,
): GridColDef<User>[] => {
  const { canPerformAction, checkRequestExists, requestPermission } = useContext(PermissionContext);
  const { Manage } = PermissionActionEnum;
  const { EmployeeManagement } = PermissionEntityEnum;
  const hasPermission = canPerformAction(Manage, EmployeeManagement);

  const sharedPermissionTooltipProps = {
    hasPermission,
    requiredPermission: { action: Manage, entity: EmployeeManagement },
    checkRequestExists,
    onRequest: requestPermission,
  }

  return [
    {
      field: "firstName",
      headerName: "First Name",
      width: 150,
    },
    {
      field: "lastName",
      headerName: "Last Name",
      width: 150,
    },
    {
      field: "email",
      headerName: "Email",
      width: 250,
    },
    {
      field: "primaryPhone",
      headerName: "Primary Phone",
      width: 180,
      renderCell: ({ value }) => <EssealPhoneInput
        size="sm"
        readOnly
        countryCode="GB"
        defaultValue={value}
        showCountrySelector={false}
        theme={{ colorBorder: 'transparent', colorBg: 'transparent' }}
      />,
    },
    {
      field: "lastSeenAt",
      headerName: "Last Seen At",
      width: 200,
      renderCell: ({ value }: GridRenderCellParams<User>) =>
        value ? new Date(value).toLocaleString() : "-",
    },
    {
      field: "status",
      headerName: "Action",
      width: 170,
      renderCell: ({ row }: GridRenderCellParams<User>) => {
        if (row.id === currentUserId) return <></>
        const isActive = row.companyEmployment?.status === EmploymentStatusEnum.Active;

        return (
          <RequestPermissionTooltip
            {...sharedPermissionTooltipProps}
            title={`Switch the user's employment to ${isActive ? 'inactive' : 'active'}`}
          >
            <Button
              variant="outlined"
              onClick={() => handleOnClickRemoveResource(row)}
              fullWidth
              size="small"
              startIcon={isActive ? <ClearOutlined /> : <RestoreOutlined />}
              disabled={!hasPermission}
            >
              {isActive ? 'Disable' : 'Reactivate'}
            </Button>
          </RequestPermissionTooltip>
        )
      }
    },
   {
      field: "loginEnabled",
      headerName: "Login Enabled",
      width: 120,
      renderCell: ({ row }: GridRenderCellParams<User>) => {
        if (row.id === currentUserId) return <></>

        return <RequestPermissionTooltip
          {...sharedPermissionTooltipProps}
          title="Enable or disable a user's ability to log into their account"
        >
         <Checkbox
          checked={row.companyEmployment?.loginEnabled as boolean} onChange={(_, checked) =>
            handleOnUpdateEmployee({ ...row, companyEmployment: { ...row.companyEmployment, loginEnabled: checked } } as User, row)
          }
          disabled={row.id === currentUserId || !hasPermission}
        />
        </RequestPermissionTooltip>
      }
    },
  ]
};

export default usersColumns;
